"use client";

import React, { useRef } from "react";
import { MapPin, Search, TrendingUp, Target, Zap, ChartBar as BarChart3, Users, CircleCheck as CheckCircle2, ArrowRight, MousePointerClick, Building2 } from 'lucide-react';
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
gsap.registerPlugin(useGSAP, ScrollTrigger);
const landscapeInsights = [{
  id: 1,
  title: "Commercial Search Intent in Preston",
  badge: "High-Value Queries",
  description: "Buyers in Preston and surrounding Lancashire areas increasingly bypass generic listings in favor of specific, high-intent local queries. Capturing these searches connects you directly with decision-makers.",
  stat: "68%",
  statLabel: "Local searches result in a direct inquiry within 24 hours",
  iconName: "Search",
  highlight: "Preston buyers want direct expertise, not agency fluff."
}, {
  id: 2,
  title: "Google Map Pack Dominance",
  badge: "Local 3-Pack Focus",
  description: "The local map pack captures over 44% of total clicks for Preston service keywords. Optimizing local signals, review velocity, and localized content drives sustainable, immediate visibility.",
  stat: "3.2x",
  statLabel: "Higher click-through rate for top-3 map placements",
  iconName: "MapPin",
  highlight: "Essential for Preston professional and B2B services."
}, {
  id: 3,
  title: "AI Search & GEO Landscape",
  badge: "Future-Proof Search",
  description: "Generative Search & AI Overviews now summarize local solutions directly in search results. I structure your Preston search ecosystem so your business is cited as the primary recommendation.",
  stat: "85%",
  statLabel: "Inquiries influenced by AI engine brand mentions",
  iconName: "Zap",
  highlight: "Generative Engine Optimisation (GEO) for Lancashire."
}];
const customerBehaviors = [{
  id: 1,
  label: "Hyper-Local Keyword Precision",
  value: "Preston & Lancashire Intent",
  detail: "Searches targeting Preston specific industrial hubs, business parks (e.g. Preston Docks, Fulwood), and Lancashire region."
}, {
  id: 2,
  label: "Direct Consultant Preference",
  value: "Senior Expert Trust",
  detail: "Local business owners prefer working directly with an experienced SEO consultant rather than delegated agency juniors."
}, {
  id: 3,
  label: "Mobile & Immediate Contact",
  value: "Click-to-Call & Audits",
  detail: "72% of regional Preston searches take place on mobile devices with expectation of immediate, direct consultation pathways."
}];
export default function PrestonMarket() {
  const containerRef = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const el = containerRef.current;
    if (!el) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".animate-card", {
        y: 35
      });
      gsap.to(".animate-card", {
        opacity: 1,
        y: 0,
        duration: 0.7,
        stagger: 0.15,
        ease: "power2.out",
        scrollTrigger: {
          trigger: el,
          start: "top 80%",
          once: true
        }
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set(".animate-card", {
        opacity: 1,
        y: 0
      });
    });
    const refresh = () => ScrollTrigger.refresh();
    requestAnimationFrame(refresh);
    if (document.readyState === "complete") refresh();else window.addEventListener("load", refresh, {
      once: true
    });
    document.fonts?.ready.then(refresh);
  }, {
    scope: containerRef
  });
  return <section id="prestonmarketanalysis" data-nav="light" className="relative bg-background text-foreground py-20 md:py-28 overflow-hidden">
      <div className="absolute -top-24 -right-24 w-96 h-96 bg-secondary/60 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-accent/10 rounded-full blur-3xl pointer-events-none" />

      <div ref={containerRef} className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        <div className="max-w-3xl mx-auto text-center mb-16 md:mb-20">
          <Badge variant="secondary" className="mb-4 px-3.5 py-1 text-xs font-semibold uppercase tracking-wider bg-secondary text-secondary-foreground border border-accent/20 rounded-full">
            <Building2 className="w-3.5 h-3.5 mr-1.5 text-primary" />
            Preston Local Search Landscape
          </Badge>
          
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-serif text-primary tracking-tight mb-6">
            Preston Market Analysis & Search Opportunity
          </h2>
          
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed font-sans">
            As an independent SEO consultant, I analyze real-time search behavior across Preston and Lancashire. 
            Understanding local commercial intent allows me to position your business directly in front of active buyers without reliance on bloated agency retainers.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {landscapeInsights.map((item, i) => <div key={item.id} data-index={i} className="animate-card flex" style={{
          opacity: 0
        }}>
              <Card className="bg-card text-card-foreground border-border/80 shadow-md hover:shadow-lg transition-all duration-300 rounded-2xl flex flex-col justify-between w-full overflow-hidden group">
                <CardHeader className="p-6 md:p-8 pb-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-3 rounded-xl bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300">
                      {item.iconName === "Search" && <Search className="w-6 h-6" />}
                      {item.iconName === "MapPin" && <MapPin className="w-6 h-6" />}
                      {item.iconName === "Zap" && <Zap className="w-6 h-6" />}
                    </div>
                    <Badge variant="outline" className="text-xs bg-secondary/80 text-foreground border-accent/30 font-medium">
                      {item.badge}
                    </Badge>
                  </div>

                  <CardTitle className="text-xl font-bold font-serif text-primary mb-2">
                    {item.title}
                  </CardTitle>
                  
                  <CardDescription className="text-sm text-muted-foreground leading-relaxed font-sans">
                    {item.description}
                  </CardDescription>
                </CardHeader>

                <CardContent className="p-6 md:p-8 pt-0 flex-1 flex flex-col justify-end">
                  <div className="bg-muted/60 p-4 rounded-xl border border-border/50 mb-4">
                    <div className="text-2xl md:text-3xl font-extrabold text-primary font-serif mb-1">
                      {item.stat}
                    </div>
                    <p className="text-xs text-muted-foreground font-medium">
                      {item.statLabel}
                    </p>
                  </div>

                  <div className="flex items-start gap-2 text-xs font-medium text-foreground bg-secondary/40 p-3 rounded-lg border border-accent/20">
                    <CheckCircle2 className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                    <span>{item.highlight}</span>
                  </div>
                </CardContent>
              </Card>
            </div>)}
        </div>

        <div className="bg-card border border-border/80 rounded-3xl p-6 sm:p-10 md:p-12 shadow-xl mb-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            
            <div className="lg:col-span-5 space-y-6">
              <Badge variant="outline" className="bg-accent/10 text-accent-foreground border-accent/30 text-xs px-3 py-1 font-semibold rounded-full">
                <Target className="w-3.5 h-3.5 mr-1.5 text-accent" />
                Customer Search Behavior
              </Badge>
              
              <h3 className="text-2xl sm:text-3xl font-bold font-serif text-primary">
                How Businesses & Buyers Search in Preston
              </h3>
              
              <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                The Preston search market is defined by high intent and zero patience for low-quality content. 
                When local customers need professional solutions, they search specifically for localized expertise and direct contact options.
              </p>

              <div className="pt-2 space-y-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-primary/10 text-primary">
                    <BarChart3 className="w-4 h-4" />
                  </div>
                  <span className="text-sm font-semibold text-foreground">Data-backed Preston local keywords</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-accent/20 text-accent-foreground">
                    <Users className="w-4 h-4" />
                  </div>
                  <span className="text-sm font-semibold text-foreground">Direct access to Yassine (no agency middlemen)</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-primary/10 text-primary">
                    <TrendingUp className="w-4 h-4" />
                  </div>
                  <span className="text-sm font-semibold text-foreground">Transparent ROI tracking & local map growth</span>
                </div>
              </div>
            </div>

            <div className="lg:col-span-7 space-y-4">
              {customerBehaviors.map((behavior, i) => <div key={behavior.id} data-index={i} className="bg-background border border-border p-5 sm:p-6 rounded-2xl shadow-xs hover:border-primary/50 transition-colors duration-200">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2">
                    <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      {behavior.label}
                    </span>
                    <span className="inline-flex items-center text-xs font-bold text-primary bg-secondary/80 px-2.5 py-1 rounded-md border border-accent/20 w-fit">
                      <MousePointerClick className="w-3 h-3 mr-1" />
                      {behavior.value}
                    </span>
                  </div>
                  <p className="text-sm text-foreground/90 font-medium leading-relaxed">
                    {behavior.detail}
                  </p>
                </div>)}
            </div>

          </div>
        </div>

        <div className="bg-primary text-primary-foreground rounded-3xl p-8 sm:p-12 text-center relative overflow-hidden shadow-2xl">
          <div className="absolute top-0 right-0 -mr-16 -mt-16 w-64 h-64 bg-accent/20 rounded-full blur-2xl pointer-events-none" />
          
          <div className="max-w-2xl mx-auto space-y-6 relative z-10">
            <h3 className="text-xl sm:text-2xl font-bold font-serif text-white tracking-tight">
              Ready to Capture Search Demand in Preston?
            </h3>
            
            <p className="text-primary-foreground/90 text-sm sm:text-base leading-relaxed">
              Work directly with me to transform your Preston and Lancashire search presence. No account managers, no generic strategy—just customized, hands-on SEO expertise.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
              <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 shadow-md hover:shadow-lg transition-all text-base font-semibold px-8 py-6 rounded-xl w-full sm:w-auto">
                <Link to="/ContactPage">
                  Book a Free Consultation
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="border-primary-foreground/30 text-white hover:bg-white/10 hover:text-white text-base font-semibold px-8 py-6 rounded-xl w-full sm:w-auto">
                <Link to="/CaseStudiesPage">
                  Explore Case Studies
                </Link>
              </Button>
            </div>
          </div>
        </div>

      </div>
    </section>;
}